import { ReactElement, useState } from "react";
import { T } from "react-translator-component";
import { setErrorObj } from "../../redux/errorReducer";
import { useAppDispatch } from "../../../redux/store";
import apiServices from "../../services/apiServices";
import { convertUserImage } from "../../../util/utils";
import LoadingModal from "../../../components/LoadingModal";
import Separator from "../../../components/Separator";
import GroupIconImage from "../GroupIconImage";
import NoticeBanner from "../NoticeBanner";
import {
  Avatar, Box, Button, Checkbox, FormControlLabel,
  Modal, TextField, Typography
} from "@mui/material";
import { colors } from "../../../common/colors";
import SubmitButton from "../../../components/SubmitButton";

interface Props {
  open: boolean;
  post: any;
  onCompleted?: () => void;
  onClose: () => void;
}

export default function PostReportModal({open, post, onCompleted, onClose}: Props): ReactElement {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [isOtherReason, setIsOtherReason] = useState<boolean>(false);
  const [reason, setReason] = useState<string>("");
  const [messages, setMessages] = useState<string[]>([]);
  const [showMessageBanner, setShowMessageBanner] = useState<boolean>(false);

  const defaultReportReason = "投稿内容に不適切な内容が含まれているため。"
  const creator: any = post?.creator

  const handleSubmit = async () => {
    try {
      setLoading(true);
      await apiServices.createPostReport(post.id, isOtherReason ? reason : defaultReportReason);
      if (onCompleted) onCompleted()
      setMessages(T("mng_report_success"));
      setShowMessageBanner(true)
      setTimeout(() => {
        onClose()
      }, 3000)
      return true
    } catch (error: any) {
      console.error(error);
      dispatch(setErrorObj(error))
      return false
    } finally {
      setLoading(false);
    }
  };

  const postCreatorInfo = () => {
    let name;
    if (post.member_group) {
      name = post.member_group.name
    } else {
      name = creator?.nickname
    }

    return (
      <div style={styles.personalInfo}>
        {post.member_group ? (
          <GroupIconImage
            groupId={post?.member_group?.id}
            alt={post?.member_group?.name}
            style={styles.personalImage}
          />
        ) : (
          <Avatar
            src={convertUserImage(creator?.image)}
            sx={styles.personalImage}
          />
        )}
        <Typography style={styles.creatorName}>{name}</Typography>
      </div>
    )
  }

  const handleClose = () => {
    setShowMessageBanner(false)
    onClose()
  }

  return (
    <>
      <LoadingModal visible={loading} />
      <Modal sx={styles.modal} open={open} onClose={handleClose}>
        <Box sx={styles.container}>
          <div style={styles.titleSection}>
            <Typography style={styles.title}>{post.title}</Typography>
          </div>

          <Separator color={colors.farm_gray100} />

          <div style={styles.reportContainer}>
            <div style={styles.personalInfo}>
              {postCreatorInfo()}
            </div>

            <Typography style={styles.howReason}>
              {T("mng_report_reason_label")}
            </Typography>

            <Separator color={colors.farm_green500} sx={{ height: "1.5px" }} />

            <div style={styles.formContainer}>
              <div>
                <FormControlLabel
                  control={
                    <Checkbox checked={!isOtherReason} sx={styles.checkBox} onChange={() => setIsOtherReason(false)} />
                  }
                  color="error"
                  sx={styles.formCheck}
                  label={
                    <Typography style={styles.checkLabel}>
                      {defaultReportReason}
                    </Typography>
                  }
                />
              </div>
              <div>
                <FormControlLabel
                  control={
                    <Checkbox checked={isOtherReason} onChange={() => setIsOtherReason(true)} />
                  }
                  color="error"
                  sx={styles.formCheck}
                  label={
                    <Typography style={styles.checkLabel}>
                      {T("mng_other")}
                    </Typography>
                  }
                />
              </div>

              <TextField
                style={styles.inputFieldTxt}
                multiline
                rows={3}
                value={reason}
                disabled={!isOtherReason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={T("mng_report_placeholder")[0]}
              />
            </div>
          </div>

          <Box sx={styles.buttonBox}>
            <SubmitButton
              sx={styles.buttonStyle}
              validate={async () => true}
              onSubmit={handleSubmit}
            >
              {T("mng_report")}
            </SubmitButton>

            <Button
              sx={styles.noButton}
              onClick={handleClose}
            >
              {T('mng_close')}
            </Button>
          </Box>
        </Box>
      </Modal>

      <NoticeBanner
        open={showMessageBanner}
        messageList={messages}
        onClose={() => setShowMessageBanner(false)}
      />
    </>
  );
}

const styles = {
  modal: {
    zIndex: 9998,
  },

  container: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "30%",
    minWidth: "400px",
    maxHeight: "90%",
    bgcolor: "background.paper",
    boxShadow: 24,
    padding: 0,
    borderRadius: "15px",
  },

  titleSection: {
    marginLeft: 10,
    marginBottom: 16,
  },

  personalInfo: {
    display: "flex",
    alignItems: "center",
    marginTop: 20,
  },

  reportContainer: {
    margin: "0 20px"
  },

  personalImage: {
    height: 50,
    width: 50,
    marginRight: "10px",
    borderRadius: "50%"
  },

  title: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: "50px",
    textAlign: "center" as const,
  },

  creatorName: {
    fontSize: 18,
    lineHeight: "50px",
  },

  howReason: {
    marginTop: 25,
    marginBottom: 15,
    fontSize: 15,
  },

  formContainer: {
    margin: "0 10px",
  },

  formCheck: {
    marginTop: "20px",
    marginLeft: "-10px",
    marginRight: 0,
  },

  checkBox: {
    padding: "0 9px 0",
  },

  checkLabel: {
    fontSize: 16,
    width: "100%"
  },

  inputFieldTxt: {
    marginTop: "20px",
    fontSize: 16,
    width: "100%"
  },

  buttonBox: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "30px",
    marginBottom: 0,
  },

  noButton: {
    cursor: "pointer",
    width: "50%",
    textAlign: "center",
    border: "0.5px solid gray",
    borderRight: "none",
    borderBottom: "none",
    borderRadius: 0,
    padding: "10px",
    color: colors.farm_blue200,
    fontWeight: "bold",
  },

  buttonStyle: {
    cursor: "pointer",
    width: "50%",
    textAlign: "center",
    border: "0.5px solid gray",
    borderRight: "none",
    borderBottom: "none",
    borderRadius: 0,
    padding: "10px",
    color: colors.farm_blue200,
    fontWeight: "bold",
    borderLeft: "none"
  },
}
